import { useState, useEffect, useRef } from 'react'

import { decodeSvgBuffer }  from '../../../shared/utils/svgUtils.js'
import { Logger }           from '../../../shared/utils/logger.js'
import { FETCH_STATUS }     from '../../../shared/constants/fetchStatus.js'

import { processTierSvg }   from '../utils/processTierSvg.js'

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useTierSvgFiles
 *
 * Fetches, decodes, processes (processTierSvg), and caches SVG files for all
 * tiers of the currently active building.
 *
 * Cache key: `${buildingId}-${tierIndex}` — persists across building switches
 * within the same component lifetime (useRef).
 *
 * SVG filename convention: `${buildingCadId}-${tierIndex.toString().padStart(2,'0')}.svg`
 * located at `${projectDataUrl}tiers/`.
 *
 * @param {object}   params
 * @param {string}   params.projectDataUrl  — base URL (trailing slash added if missing)
 * @param {object[]} params.buildings       — from useCatalog().buildings
 * @param {object[]} params.tiers           — from useCatalog().tiers  (already filtered by buildTiersData)
 * @param {string|null} params.activeBuilding — building id_cad of the active building
 *
 * @returns {{ tiersLayers: (string|null)[], svgStatus: string }}
 *   `tiersLayers` — indexed array parallel to the tiers of activeBuilding;
 *                   each entry is processed outerHTML or null on failure
 *   `svgStatus`   — one of FETCH_STATUS.*
 */
export function useTierSvgFiles({ projectDataUrl, buildings, tiers, activeBuilding }) {
  const cache = useRef({})

  const [tiersLayers, setTiersLayers] = useState({})
  const [svgStatus,   setSvgStatus]   = useState(FETCH_STATUS.LOADING)

  useEffect(() => {
    if (!projectDataUrl || !buildings?.length || !tiers?.length || !activeBuilding) {
      setTiersLayers([])
      setSvgStatus(FETCH_STATUS.LOADING)
      return
    }

    const building = buildings.find(b => b.id_cad === activeBuilding)
    if (!building) {
      setTiersLayers([])
      setSvgStatus(FETCH_STATUS.ERROR)
      return
    }

    // Tiers for this building, in ref_tier order (buildTiersData preserves buildings.json order)
    const buildingTiers = tiers.filter(t => t.building_id === activeBuilding)
    if (!buildingTiers.length) {
      setTiersLayers([])
      setSvgStatus(FETCH_STATUS.ERROR)
      return
    }

    // Check if all tiers are already cached
    const allCached = buildingTiers.every((_, idx) => {
      const key = `${activeBuilding}-${idx}`
      return key in cache.current
    })
    if (allCached) {
      const layers = {}
      buildingTiers.forEach((tier, idx) => {
        layers[tier.ref_tier] = cache.current[`${activeBuilding}-${idx}`]
      })
      setTiersLayers(layers)
      setSvgStatus(FETCH_STATUS.READY)
      return
    }

    let cancelled = false
    setSvgStatus(FETCH_STATUS.LOADING)

    const base = projectDataUrl.endsWith('/') ? projectDataUrl : projectDataUrl + '/'

    async function fetchAll() {
      const layers = {}

      for (const [tierIndex, tier] of buildingTiers.entries()) {
        const cacheKey = `${activeBuilding}-${tierIndex}`

        // Serve from cache if available
        if (cacheKey in cache.current) {
          layers[tier.ref_tier] = cache.current[cacheKey]
          continue
        }

        const filename = `${activeBuilding}-${String(tierIndex).padStart(2, '0')}.svg`
        const url      = `${base}tiers/${filename}`

        try {
          const response = await fetch(url)

          if (cancelled) return

          if (!response.ok) {
            Logger.error({ message: `useTierSvgFiles: fetch failed`, content: `${response.status} ${url}` })
            layers.push(null)
            cache.current[cacheKey] = null
            continue
          }

          const buffer  = await response.arrayBuffer()
          if (cancelled) return

          const svgText = decodeSvgBuffer(buffer)
          const parser  = new DOMParser()
          const svgDoc  = parser.parseFromString(svgText, 'image/svg+xml')
          const svgEl   = svgDoc.querySelector('svg')

          const processed = processTierSvg(svgEl)
          layers[tier.ref_tier] = processed
          cache.current[cacheKey] = processed

        } catch (err) {
          if (cancelled) return
          Logger.error({ message: `useTierSvgFiles: error fetching ${url}`, content: err })
          layers[tier.ref_tier] = null
          cache.current[cacheKey] = null
        }
      }

      if (!cancelled) {
        setTiersLayers(layers)
        setSvgStatus(FETCH_STATUS.READY)
      }
    }

    fetchAll().catch(err => {
      if (!cancelled) {
        Logger.error({ message: 'useTierSvgFiles: unexpected error', content: err })
        setSvgStatus(FETCH_STATUS.ERROR)
      }
    })

    return () => { cancelled = true }
  }, [projectDataUrl, buildings, tiers, activeBuilding])

  return { tiersLayers, svgStatus }
}
